import React, { useState, useEffect } from 'react';
import { LogOut, User as UserIcon, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { UserSettingsModal } from './UserSettings';

interface UserProfileProps {
  selectedTeamId?: string | null;
}

export const UserProfile: React.FC<UserProfileProps> = ({ selectedTeamId }) => {
  const { user, logout, authConfig } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'profile' | 'team' | 'integrations' | 'ai'>('profile');

  // Auto-open settings modal on integrations tab when redirected from Google OAuth
  useEffect(() => {
    if (user && window.location.pathname === '/settings') {
      setSettingsTab('integrations');
      setShowSettingsModal(true);
      // Don't clear URL here - let IntegrationsTab process the auth code first
      // IntegrationsTab will clear the URL params after processing
    }
  }, [user]);

  if (!user) {
    return null;
  }

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
  };

  const handleSettings = () => {
    setShowDropdown(false);
    // Small delay to ensure dropdown closes before modal opens
    setTimeout(() => {
      setSettingsTab('ai');
      setShowSettingsModal(true);
    }, 100);
  };

  const isAuthDisabled = authConfig && !authConfig.auth_enabled;

  return (
    <>
      <div className="relative">
              <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
          {user.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
              <UserIcon className="w-5 h-5 text-gray-600" />
            </div>
          )}
          <div className="text-left hidden sm:block">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
          </div>
        </button>

        {showDropdown && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowDropdown(false)}
            />
            
            {/* Dropdown */}
            <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-12 h-12 rounded-full"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                      <UserIcon className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{user.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                    {isAuthDisabled && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Development Mode</div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-2">
                <button
                  onClick={handleSettings}
                  className="w-full flex items-center space-x-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </button>
                
                {!isAuthDisabled && (
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-2 px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign out</span>
                  </button>
                )}
                
                {isAuthDisabled && (
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    Authentication is disabled in development mode
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Settings Modal */}
      <UserSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        selectedTeamId={selectedTeamId}
        initialTab={settingsTab}
      />
    </>
  );
}; 